import api from "../api";

const BASE_URL = "/community-programs";

export interface CommunityProgram {
  backgroundColor?: string;
  beneficiaries: {
    count: number;
    label: string;
  };
  buttonText: string;
  buttonUrl: string;
  category: string;
  createdAt?: string;
  description: string;
  featured: boolean;
  hidden?: boolean;
  id: string;
  imageAlt: string;
  impact: {
    label: string;
    value: string;
  };
  isActive: boolean;
  order: number;
  title: string;
  updatedAt?: string;
  updatedBy?: string;
  uploadedImageUrl?: string; // Use this for the uploaded image URL
}

function mapCommunityProgram(p: any, idx = 0): CommunityProgram {
  return {
    backgroundColor: "",
    beneficiaries: {
      count: Number(p.beneficiaries_count ?? 0),
      label: p.beneficiaries_label ?? "",
    },
    buttonText: p.button_text ?? "",
    buttonUrl: p.button_url ?? "",
    category: p.category,
    createdAt: p.created_at,
    description: p.description,
    featured: !!p.featured,
    hidden: !!p.hidden,
    id: String(p.id),
    imageAlt: p.image_alt_text ?? "",
    impact: {
      label: p.impact_label ?? "",
      value: p.impact_value ?? "",
    },
    isActive: !p.hidden,
    order: idx,
    title: p.title,
    updatedAt: p.updated_at,
    updatedBy: p.updated_by,
    uploadedImageUrl: p.image_url ?? "", // Use this field for display only
  };
}

export const CommunityProgramService = {
  create: async (
    data: CommunityProgram,
    imageFile?: File,
  ): Promise<CommunityProgram> => {
    const formData = new FormData();
    formData.append("title", data.title);
    formData.append("category", data.category);
    formData.append("description", data.description);
    formData.append("beneficiaries_count", String(data.beneficiaries.count));
    formData.append("beneficiaries_label", data.beneficiaries.label);
    formData.append("impact_value", data.impact.value);
    formData.append("impact_label", data.impact.label);
    formData.append("button_text", data.buttonText);
    formData.append("button_url", data.buttonUrl);
    formData.append("image_alt_text", data.imageAlt);
    formData.append("featured", data.featured ? "1" : "0");
    formData.append("hidden", !data.isActive ? "1" : "0");
    if (imageFile) {
      formData.append("image", imageFile); // <-- send file as 'image'
    }
    // Do NOT send image_url if uploading a file

    console.log("FormData being sent:", Object.fromEntries(formData.entries()));
    try {
      const response = await api.post(BASE_URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return mapCommunityProgram(response.data.program);
    } catch (error: any) {
      if (error.response) {
        console.error("Backend error response:", error.response.data);
        alert(JSON.stringify(error.response.data, null, 2));
      } else {
        console.error("Unknown error:", error);
      }
      throw error;
    }
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${BASE_URL}/${id}`);
  },

  feature: async (id: string): Promise<CommunityProgram> => {
    const response = await api.post(`${BASE_URL}/${id}/feature`);
    return mapCommunityProgram(response.data.program);
  },

  getAll: async (): Promise<CommunityProgram[]> => {
    const response = await api.get(BASE_URL);
    return (response.data.programs || []).map(mapCommunityProgram);
  },

  getById: async (id: string): Promise<CommunityProgram> => {
    const response = await api.get(`/community-programs/${id}`);
    return mapCommunityProgram(response.data.program);
  },

  hide: async (id: string): Promise<CommunityProgram> => {
    const response = await api.post(`${BASE_URL}/${id}/hide`);
    return mapCommunityProgram(response.data);
  },

  unfeature: async (id: string): Promise<CommunityProgram> => {
    const response = await api.post(`${BASE_URL}/${id}/unfeature`);
    return mapCommunityProgram(response.data.program);
  },

  unhide: async (id: string): Promise<CommunityProgram> => {
    const response = await api.post(`${BASE_URL}/${id}/unhide`);
    return mapCommunityProgram(response.data);
  },

  update: async (
    id: string,
    data: CommunityProgram,
    imageFile?: File,
  ): Promise<CommunityProgram> => {
    const formData = new FormData();
    formData.append("title", data.title);
    formData.append("category", data.category);
    formData.append("description", data.description);
    formData.append("beneficiaries_count", String(data.beneficiaries.count));
    formData.append("beneficiaries_label", data.beneficiaries.label);
    formData.append("impact_value", data.impact.value);
    formData.append("impact_label", data.impact.label);
    formData.append("image_alt_text", data.imageAlt);
    formData.append("featured", data.featured ? "1" : "0");
    formData.append("hidden", data.isActive ? "0" : "1");
    formData.append("button_text", data.buttonText ?? "");
    formData.append("button_url", data.buttonUrl ?? "");
    if (imageFile) {
      formData.append("image", imageFile);
      formData.append("image_alt_text", data.imageAlt);
    } else if (data.uploadedImageUrl) {
      formData.append("image_url", data.uploadedImageUrl);
      formData.append("image_alt_text", data.imageAlt); // <-- Always send this!
    }
    return api.post(`/community-programs/${id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  // Add an image upload method if needed
  uploadImage: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("image", file);
    const response = await api.post(`${BASE_URL}/upload-image`, formData);
    return response.data.image_url; // Use this URL in uploadedImageUrl
  },
};
